import { ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from '@mui/material';
import { ArticleOutlined, BrushOutlined } from '@mui/icons-material';
import { TOUR } from '@features/onboarding/tourTargets';

interface BaseViewToggleProps {
  value: 'reading' | 'canvas';
  onChange: (value: 'reading' | 'canvas') => void;
}

/**
 * BaseViewToggle - Toggle button group for switching between Reading and Canvas views
 * Replaces the previous StyledSwitch with a clearer, more accessible control
 */
export default function BaseViewToggle({ value, onChange }: BaseViewToggleProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: 'reading' | 'canvas' | null
  ) => {
    // Prevent deselection - one option must always be active
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  return (
    <ToggleButtonGroup
      data-tour={TOUR.baseViewToggle}
      value={value}
      exclusive
      onChange={handleChange}
      aria-label="base view mode"
      size={isMobile ? 'small' : 'medium'}
      sx={{
        '& .MuiToggleButton-root': {
          px: isMobile ? 1 : 2,
          py: 0.5,
        },
      }}
    >
      <ToggleButton
        value="reading"
        aria-label="reading mode"
        title="Switch to Reading Mode (Ctrl+1)"
      >
        <ArticleOutlined sx={{ mr: isMobile ? 0 : 1 }} fontSize="small" />
        {!isMobile && 'Reading'}
      </ToggleButton>
      <ToggleButton
        value="canvas"
        aria-label="canvas mode"
        title="Switch to Canvas Mode (Ctrl+2)"
      >
        <BrushOutlined sx={{ mr: isMobile ? 0 : 1 }} fontSize="small" />
        {!isMobile && 'Canvas'}
      </ToggleButton>
    </ToggleButtonGroup>
  );
}
